'use client'

import { cn } from '@/lib/utils'

/**
 * AxisPills — Bill prototype 2026-05-30 (.axisrow + .axis).
 *
 * Filter/sort affordances. Selecting a pill (Channels / Subchannels /
 * Commands / Your prompts) sorts that section to the top of the page,
 * directly below this row; "All" restores the default order. State lives
 * in MarketplacePage and is passed down via `active` + `onSelect`. NOT
 * gated — anonymous visitors should be able to scope the page just like
 * signed-in ones can. The row is centered (justify-center) to match the
 * prototype's .axisrow rule.
 */

export type AxisId = 'all' | 'channels' | 'subchannels' | 'commands' | 'your-prompts'

export interface AxisCounts {
  channels: number
  subchannels: number
  commands: number
  prompts: number
}

export function AxisPills({
  counts,
  active,
  onSelect,
}: {
  counts: AxisCounts
  active: AxisId
  onSelect: (id: AxisId) => void
}) {
  const pills: Array<{ id: AxisId; label: string; count?: number }> = [
    { id: 'all', label: 'All' },
    { id: 'channels', label: 'Channels', count: counts.channels },
    { id: 'subchannels', label: 'Subchannels', count: counts.subchannels },
    { id: 'commands', label: 'Commands', count: counts.commands },
    counts.prompts > 0
      ? { id: 'your-prompts', label: 'Your prompts', count: counts.prompts }
      : { id: 'your-prompts', label: 'Your prompts' },
  ]

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 px-6 pt-[22px]">
      {pills.map((p) => {
        const isActive = p.id === active
        return (
          <button
            key={p.id}
            type="button"
            aria-pressed={isActive}
            onClick={() => onSelect(isActive ? 'all' : p.id)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-[15px] py-2 text-[13px] font-semibold transition-colors',
              isActive
                ? 'border border-text-strong bg-text-strong text-surface'
                : 'border-[0.5px] border-line bg-surface text-text hover:border-brand hover:text-brand',
            )}
          >
            <span>{p.label}</span>
            {typeof p.count === 'number' && (
              <span
                className={cn(
                  'font-mono text-[11px]',
                  isActive ? 'text-surface/60' : 'text-text-muted',
                )}
              >
                {p.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
